import { z } from 'zod'

// Audit entity type enum
export enum AuditEntityType {
  ORGANIZATION = 'ORGANIZATION',
  USER = 'USER',
  MEMBER = 'MEMBER',
  ENTITY = 'ENTITY',
  PROJECT = 'PROJECT',
  TASK = 'TASK',
  TICKET = 'TICKET',
  DEAL = 'DEAL',
  CONTACT = 'CONTACT',
  EVENT = 'EVENT',
  CONTRACT = 'CONTRACT',
  INVOICE = 'INVOICE',
  THREAD = 'THREAD',
  TIME_ENTRY = 'TIME_ENTRY',
  TEAM = 'TEAM',
  INVENTORY = 'INVENTORY',
  SUPPLIER = 'SUPPLIER',
  DOCUMENT = 'DOCUMENT',
  CUSTOM_FIELD = 'CUSTOM_FIELD',
  CUSTOM_COLLECTION = 'CUSTOM_COLLECTION',
  PERMISSION = 'PERMISSION',
  PERMISSION_SET = 'PERMISSION_SET',
  FEATURE_FLAG = 'FEATURE_FLAG',
  BILLING_AUTHORIZATION = 'BILLING_AUTHORIZATION',
  SUPPLIER_CONTRACT = 'SUPPLIER_CONTRACT',
  SUPPLIER_AUTHORIZATION = 'SUPPLIER_AUTHORIZATION',
  SUPPLIER_BILL = 'SUPPLIER_BILL',
}

// Compliance standard type
export enum ComplianceStandard {
  GDPR = 'GDPR',
  SOC2 = 'SOC2',
  HIPAA = 'HIPAA',
  ISO27001 = 'ISO27001',
  CUSTOM = 'CUSTOM',
}

// Audit action enum
export enum AuditActionEnum {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
  ACCESS = 'ACCESS',
  EXPORT = 'EXPORT',
  IMPORT = 'IMPORT',
  BULK_UPDATE = 'BULK_UPDATE',
  BULK_DELETE = 'BULK_DELETE',
}

// Audit retention period enum
export enum AuditRetentionPeriodEnum {
  DAYS_30 = 'DAYS_30',
  DAYS_90 = 'DAYS_90',
  DAYS_180 = 'DAYS_180',
  YEAR_1 = 'YEAR_1',
  YEARS_2 = 'YEARS_2',
  YEARS_5 = 'YEARS_5',
  YEARS_7 = 'YEARS_7',
  PERMANENT = 'PERMANENT',
}

// Audit log schema
export const AuditLogSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  userId: z.string().nullable(),
  userEmail: z.string().nullable(),
  userName: z.string().nullable(),
  impersonatorId: z.string().nullable(),
  entityType: z.string(),
  entityId: z.string(),
  entityName: z.string().nullable(),
  action: z.nativeEnum(AuditActionEnum),
  actionDetails: z.string().nullable(),
  moduleType: z.string().nullable(),
  moduleId: z.string().nullable(),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  requestId: z.string().nullable(),
  sessionId: z.string().nullable(),
  duration: z.number().nullable(),
  reason: z.string().nullable(),
  approvedBy: z.string().nullable(),
  ticketRef: z.string().nullable(),
  createdAt: z.date(),
})

export type AuditLog = z.infer<typeof AuditLogSchema>

// Audit change schema
export const AuditChangeSchema = z.object({
  id: z.string(),
  auditLogId: z.string(),
  fieldName: z.string(),
  fieldType: z.string(),
  oldValue: z.any().nullable(),
  newValue: z.any().nullable(),
  isCompressed: z.boolean(),
})

export type AuditChange = z.infer<typeof AuditChangeSchema>

// Audit context schema
export const AuditContextSchema = z.object({
  userId: z.string().optional(),
  userEmail: z.string().optional(),
  userName: z.string().optional(),
  impersonatorId: z.string().optional(),
  organizationId: z.string(),
  request: z.any().optional(),
  reason: z.string().optional(),
  approvedBy: z.string().optional(),
  ticketRef: z.string().optional(),
})

export type AuditContext = z.infer<typeof AuditContextSchema>

// Change detail schema
export const ChangeDetailSchema = z.object({
  fieldName: z.string(),
  fieldType: z.string(),
  oldValue: z.any(),
  newValue: z.any(),
})

export type ChangeDetail = z.infer<typeof ChangeDetailSchema>

// Audit filters schema
export const AuditFiltersSchema = z.object({
  entityType: z.string().optional(),
  entityId: z.string().optional(),
  userId: z.string().optional(),
  action: z.nativeEnum(AuditActionEnum).optional(),
  startDate: z.date().optional(),
  endDate: z.date().optional(),
  moduleType: z.string().optional(),
  moduleId: z.string().optional(),
})

export type AuditFilters = z.infer<typeof AuditFiltersSchema>

// Audit log with changes schema
export const AuditLogWithChangesSchema = AuditLogSchema.extend({
  changes: z.array(AuditChangeSchema),
})

export type AuditLogWithChanges = z.infer<typeof AuditLogWithChangesSchema>

// Audit summary schema
export const AuditSummarySchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  periodStart: z.date(),
  periodEnd: z.date(),
  periodType: z.enum(['hour', 'day', 'month']),
  entityType: z.string(),
  action: z.nativeEnum(AuditActionEnum),
  count: z.number(),
  uniqueUsers: z.number(),
  avgDuration: z.number().nullable(),
  topEntities: z
    .array(
      z.object({
        entityId: z.string(),
        count: z.number(),
      }),
    )
    .nullable(),
  topUsers: z
    .array(
      z.object({
        userId: z.string(),
        count: z.number(),
      }),
    )
    .nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
})

export type AuditSummary = z.infer<typeof AuditSummarySchema>

// Audit retention policy schema
export const AuditRetentionPolicySchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  entityType: z.string().nullable(),
  action: z.nativeEnum(AuditActionEnum).nullable(),
  retentionPeriod: z.nativeEnum(AuditRetentionPeriodEnum),
  archiveEnabled: z.boolean(),
  archiveAfterDays: z.number().nullable(),
  deleteEnabled: z.boolean(),
  lastProcessed: z.date().nullable(),
  isActive: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date(),
})

export type AuditRetentionPolicy = z.infer<typeof AuditRetentionPolicySchema>

// Audit access log schema
export const AuditAccessLogSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  userId: z.string(),
  userEmail: z.string(),
  accessType: z.enum(['view', 'export', 'report']),
  filters: z.any().nullable(),
  entityTypes: z.array(z.string()),
  dateRangeStart: z.date().nullable(),
  dateRangeEnd: z.date().nullable(),
  recordCount: z.number().nullable(),
  exportFormat: z.string().nullable(),
  exportPath: z.string().nullable(),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  createdAt: z.date(),
})

export type AuditAccessLog = z.infer<typeof AuditAccessLogSchema>

// Query audit logs input schema
export const QueryAuditLogsSchema = z.object({
  organizationId: z.string(),
  filters: AuditFiltersSchema,
  pagination: z.object({
    page: z.number().min(1).default(1),
    limit: z.number().min(1).max(100).default(20),
  }),
})

export type QueryAuditLogsInput = z.infer<typeof QueryAuditLogsSchema>

// Generate compliance report input schema
export const GenerateComplianceReportSchema = z.object({
  organizationId: z.string(),
  reportType: z.enum(['GDPR', 'SOC2', 'HIPAA', 'ISO27001']),
  dateRange: z.object({
    start: z.date(),
    end: z.date(),
  }),
})

export type GenerateComplianceReportInput = z.infer<
  typeof GenerateComplianceReportSchema
>

// Export audit logs input schema
export const ExportAuditLogsSchema = z.object({
  organizationId: z.string(),
  format: z.enum(['CSV', 'JSON', 'PDF']),
  filters: AuditFiltersSchema.optional(),
  dateRange: z
    .object({
      start: z.date(),
      end: z.date(),
    })
    .optional(),
})

export type ExportAuditLogsInput = z.infer<typeof ExportAuditLogsSchema>

// ---------------------------------------------------------------------------
// AuditActionDetails — discriminated union for domain-level audit discriminators
// ---------------------------------------------------------------------------
// The `action` field is the machine-parseable prefix stored at the start of the
// `actionDetails` column (format: "DOMAIN.VERB_CONTEXT — human context").
// Use `formatAuditActionDetails` to serialize to the DB string.
// ---------------------------------------------------------------------------

export type AuditActionDetails =
  | {
      action: 'INVOICE.CREATE_FROM_TIME'
      count: number
      projectId: string
      total: number
      currency: string
    }
  | { action: 'SUPPLIER.CREATE'; code: string; name: string }
  | { action: 'SUPPLIER.UPDATE'; code: string; name: string }
  | { action: 'SUPPLIER.DELETE'; code: string; name: string }
  | { action: 'PURCHASE_ORDER.CREATE'; orderNumber: string }
  | { action: 'PURCHASE_ORDER.UPDATE'; orderNumber: string }
  | {
      action: 'PURCHASE_ORDER.UPDATE_STATUS'
      orderNumber: string
      oldStatus: string
      newStatus: string
    }
  | { action: 'PURCHASE_ORDER.DELETE'; orderNumber: string }
  | { action: 'TASK.STATUS_CHANGED' }
  | {
      action: 'TIME_ENTRY.EXPORT'
      format: string
      recordCount: number
      filters?: Record<string, unknown> | null
      dateRange?: { start?: string; end?: string } | null
      groupBy?: string | null
      statusTier?: string | null
      statusFilter?: string[] | null
      columns?: string[] | null
    }
  | {
      action: 'BUDGET.EXPORT'
      projectId: string
      format: string
      groupBy: string
      recordCount: number
    }
  | { action: 'CUSTOM_FIELD.UPDATED'; fieldLabel: string }
  | { action: 'MEETING_RECORDING.DELETE'; recordingId: string }
  | {
      action: 'MEETING_RECORDING.PURGE'
      recordingId: string
      audioObjects: number
      documents: number
      transcripts: number
      notePages: number
    }

export function formatAuditActionDetails(details: AuditActionDetails): string {
  switch (details.action) {
    case 'INVOICE.CREATE_FROM_TIME':
      return `INVOICE.CREATE_FROM_TIME — ${details.count} time entries, project ${details.projectId}, total ${details.total.toFixed(2)} ${details.currency}`
    case 'SUPPLIER.CREATE':
      return `SUPPLIER.CREATE — ${details.code} - ${details.name}`
    case 'SUPPLIER.UPDATE':
      return `SUPPLIER.UPDATE — ${details.code} - ${details.name}`
    case 'SUPPLIER.DELETE':
      return `SUPPLIER.DELETE — ${details.code} - ${details.name}`
    case 'PURCHASE_ORDER.CREATE':
      return `PURCHASE_ORDER.CREATE — ${details.orderNumber}`
    case 'PURCHASE_ORDER.UPDATE':
      return `PURCHASE_ORDER.UPDATE — ${details.orderNumber}`
    case 'PURCHASE_ORDER.UPDATE_STATUS':
      return `PURCHASE_ORDER.UPDATE_STATUS — ${details.orderNumber}: ${details.oldStatus} -> ${details.newStatus}`
    case 'PURCHASE_ORDER.DELETE':
      return `PURCHASE_ORDER.DELETE — ${details.orderNumber}`
    case 'TASK.STATUS_CHANGED':
      return 'TASK.STATUS_CHANGED'
    case 'TIME_ENTRY.EXPORT': {
      const parts: string[] = [
        `format: ${details.format}`,
        `records: ${details.recordCount}`,
      ]
      if (details.groupBy) parts.push(`grouped by: ${details.groupBy}`)
      if (details.statusTier) parts.push(`tier: ${details.statusTier}`)
      return `TIME_ENTRY.EXPORT — ${parts.join(', ')}`
    }
    case 'BUDGET.EXPORT':
      return `BUDGET.EXPORT — project: ${details.projectId}, format: ${details.format}, grouped by: ${details.groupBy}, records: ${details.recordCount}`
    case 'CUSTOM_FIELD.UPDATED':
      return `CUSTOM_FIELD.UPDATED — "${details.fieldLabel}"`
    case 'MEETING_RECORDING.DELETE':
      return `MEETING_RECORDING.DELETE — recording ${details.recordingId}, purge requested`
    case 'MEETING_RECORDING.PURGE':
      return `MEETING_RECORDING.PURGE — recording ${details.recordingId}: ${details.audioObjects} audio object(s), ${details.documents} document(s), ${details.transcripts} transcript(s), ${details.notePages} transcript page(s) removed`
  }
}
